import dayjs from 'dayjs';
import {
  SURVEY_AFTER_DAYS, assigneeOf, complaintMetaOf, customerByPhone, customerById, isAftercareTask, isMedicalTask, isSurveyTask, npsOf,
  ownerOf, surveyMin, surveyStateOf, type Appointment, type Callback, type ComplaintCategory, type Severity, type State, type Survey,
} from '../CustomerDemo/callCenterStore';

// ============================ Công việc ============================

export type TaskType = 'medical' | 'complaint' | 'survey' | 'aftercare' | 'callback';
export const TASK_TYPE: Record<TaskType, string> = {
  medical: 'Bất thường', complaint: 'Khiếu nại', survey: 'Khảo sát', aftercare: 'Hỏi thăm sau DV', callback: 'Gọi lại',
};
export const taskTypeOf = (cb: Callback): TaskType =>
  isMedicalTask(cb) ? 'medical' : complaintMetaOf(cb) ? 'complaint' : isSurveyTask(cb) ? 'survey' : isAftercareTask(cb) ? 'aftercare' : 'callback';

export type TaskTab = 'overdue' | 'today' | 'upcoming' | 'done';
export const TASK_TAB: Record<TaskTab, string> = { overdue: 'Quá hạn', today: 'Hôm nay', upcoming: 'Sắp tới', done: 'Đã xong (7 ngày)' };

export function taskTabOf(cb: Callback, now = Date.now()): TaskTab | null {
  if (cb.doneAt) return cb.doneAt >= now - 7 * 86400000 ? 'done' : null;
  if (cb.dueAt < now) return 'overdue';
  return cb.dueAt <= dayjs(now).endOf('day').valueOf() ? 'today' : 'upcoming';
}

export interface TaskFilter { type?: TaskType | null; assignee?: string | null; q?: string }

const matchQ = (s: State, phone: string, q?: string, extra = '') => {
  const k = (q || '').trim().toLowerCase();
  if (!k) return true;
  const name = customerByPhone(s, phone)?.name || '';
  return `${name} ${phone} ${extra}`.toLowerCase().includes(k);
};

export function filterTasks(s: State, f: TaskFilter) {
  return s.callbacks.filter(cb =>
    (!f.type || taskTypeOf(cb) === f.type) &&
    (!f.assignee || assigneeOf(s, cb) === f.assignee) &&
    matchQ(s, cb.phone, f.q, cb.note));
}

export function taskCounts(list: Callback[], now = Date.now()): Record<TaskTab, number> {
  const out: Record<TaskTab, number> = { overdue: 0, today: 0, upcoming: 0, done: 0 };
  list.forEach(cb => { const t = taskTabOf(cb, now); if (t) out[t]++; });
  return out;
}

/** Việc gấp xếp trước: bất thường → khiếu nại → còn lại; cùng loại thì hạn sớm trước. Đã xong: mới nhất trước */
const TYPE_RANK: Record<TaskType, number> = { medical: 0, complaint: 1, survey: 2, aftercare: 2, callback: 3 };
export function sortTasks(list: Callback[], tab: TaskTab) {
  return [...list].sort((a, b) => (tab === 'done' ? (b.doneAt || 0) - (a.doneAt || 0) : TYPE_RANK[taskTypeOf(a)] - TYPE_RANK[taskTypeOf(b)] || a.dueAt - b.dueAt));
}

// ============================ Khiếu nại ============================

export type ComplaintStatus = 'open' | 'overdue' | 'done';
export const COMPLAINT_STATUS: Record<ComplaintStatus, string> = { open: 'Đang xử lý', overdue: 'Quá hạn SLA', done: 'Đã đóng' };

export const complaintStatusOf = (cb: Callback, now = Date.now()): ComplaintStatus =>
  cb.doneAt ? 'done' : cb.dueAt < now ? 'overdue' : 'open';

export interface ComplaintFilter { status?: ComplaintStatus | 'active' | null; category?: ComplaintCategory | null; severity?: Severity | null; q?: string }

export function complaintsOf(s: State, f: ComplaintFilter = {}, now = Date.now()) {
  const sevRank: Record<Severity, number> = { high: 0, medium: 1, low: 2 };
  return s.callbacks
    .filter(cb => {
      const m = complaintMetaOf(cb);
      if (!m) return false;
      const st = complaintStatusOf(cb, now);
      if (f.status === 'active' ? st === 'done' : f.status && st !== f.status) return false;
      return (!f.category || m.category === f.category) && (!f.severity || m.severity === f.severity) && matchQ(s, cb.phone, f.q, cb.note);
    })
    .sort((a, b) => (a.doneAt ? 1 : 0) - (b.doneAt ? 1 : 0)
      || (a.doneAt ? (b.doneAt || 0) - (a.doneAt || 0) : sevRank[complaintMetaOf(a)!.severity] - sevRank[complaintMetaOf(b)!.severity] || a.dueAt - b.dueAt));
}

export function complaintStats(s: State, now = Date.now()) {
  const all = complaintsOf(s, {}, now);
  const closed = all.filter(cb => cb.doneAt);
  const rated = closed.filter(cb => typeof cb.satisfied === 'boolean');
  // Đóng đúng hạn: hoàn thành trước hạn SLA
  const onTime = closed.filter(cb => (cb.doneAt || 0) <= cb.dueAt);
  return {
    open: all.filter(cb => !cb.doneAt).length,
    overdue: all.filter(cb => complaintStatusOf(cb, now) === 'overdue').length,
    escalated: all.filter(cb => !cb.doneAt && complaintMetaOf(cb)!.level === 2).length,
    closed: closed.length,
    satisfiedRate: rated.length ? Math.round((rated.filter(cb => cb.satisfied).length / rated.length) * 100) : null,
    onTimeRate: closed.length ? Math.round((onTime.length / closed.length) * 100) : null,
  };
}

// ============================ Khảo sát ============================

/** Điểm hài lòng trung bình của 1 khảo sát (các tiêu chí sao) */
export const surveyScore = (sv: Survey) => {
  const xs = [sv.service, sv.attitude, ...(typeof sv.practitionerScore === 'number' ? [sv.practitionerScore] : [])];
  return xs.reduce((t, v) => t + v, 0) / xs.length;
};
export const isDoneSurvey = (sv: Survey) => (sv.status || 'done') === 'done';
export const isLowSurvey = (sv: Survey) => isDoneSurvey(sv) && surveyMin(sv) <= 2;

export function surveyStats(surveys: Survey[]) {
  const done = surveys.filter(isDoneSurvey);
  return {
    done: done.length,
    csat: done.length ? done.reduce((t, sv) => t + surveyScore(sv), 0) / done.length : null,
    nps: npsOf(done),
    low: done.filter(isLowSurvey).length,
    skipped: surveys.length - done.length,
  };
}

export interface PendingSurvey { appointment: Appointment; dueAt: number; attempts: number; overdue: boolean }

/** Lần làm dịch vụ đã xong nhưng chưa khảo sát (đến hạn sau SURVEY_AFTER_DAYS ngày) */
export function pendingSurveys(s: State, now = Date.now()): PendingSurvey[] {
  return s.appointments
    .filter(a => a.status === 'done' && customerById(s, a.customerId))
    .map(a => ({ a, st: surveyStateOf(s, a.id) }))
    .filter(x => x.st.state === 'pending')
    .map(({ a, st }) => {
      const dueAt = dayjs(a.at).add(SURVEY_AFTER_DAYS, 'day').startOf('day').valueOf();
      return { appointment: a, dueAt, attempts: st.attempts, overdue: dueAt < dayjs(now).startOf('day').valueOf() };
    })
    .sort((x, y) => x.dueAt - y.dueAt);
}

// ============================ Hiệu suất nhân viên ============================

export interface AgentRow { agent: string; calls: number; outgoing: number; answered: number; talk: number; tasksDone: number; tasksOpen: number }

export function agentStats(s: State, [from, to]: [number, number], now = Date.now()): AgentRow[] {
  const map = new Map<string, AgentRow>();
  const row = (agent: string) => {
    if (!map.has(agent)) map.set(agent, { agent, calls: 0, outgoing: 0, answered: 0, talk: 0, tasksDone: 0, tasksOpen: 0 });
    return map.get(agent)!;
  };
  s.calls.filter(c => c.startedAt >= from && c.startedAt <= to).forEach(c => {
    const r = row(c.agent);
    r.calls++;
    if (c.direction === 'out') r.outgoing++;
    if (c.outcome === 'answered') { r.answered++; r.talk += c.duration; }
  });
  s.callbacks.forEach(cb => {
    if (cb.doneAt && cb.doneBy && cb.doneAt >= from && cb.doneAt <= to) row(cb.doneBy).tasksDone++;
    if (!cb.doneAt && cb.dueAt <= now) row(assigneeOf(s, cb)).tasksOpen++;
  });
  return Array.from(map.values()).sort((a, b) => b.calls - a.calls || b.tasksDone - a.tasksDone);
}

// ============================ Tổng quan (theo người xem) ============================

/** Mục tiêu số cuộc gọi mỗi nhân viên / ngày */
export const DAILY_CALL_TARGET = 20;
const HOUR = 3600000;
const DAY = 24 * HOUR;

/**
 * Số liệu trang Tổng quan, lọc theo người (null = cả nhóm):
 * việc theo người thực hiện, cuộc gọi theo nhân viên gọi, khảo sát / lịch hẹn theo nhân viên phụ trách khách.
 */
export function dashboardData(s: State, who: string | null, now = Date.now()) {
  const today = dayjs(now).startOf('day').valueOf();
  const ownsCustomer = (customerId: number) => !who || ownerOf(customerById(s, customerId)) === who;
  const callsBetween = (from: number, to: number) => s.calls.filter(c => c.startedAt >= from && c.startedAt <= to && (!who || c.agent === who));

  const callsToday = callsBetween(today, now);
  // So với cùng khung giờ hôm qua cho công bằng
  const callsYesterday = callsBetween(today - DAY, now - DAY);
  const missed = callsToday.filter(c => c.outcome !== 'answered').length;

  const tasks = s.callbacks.filter(cb => !who || assigneeOf(s, cb) === who);
  const open = tasks.filter(cb => !cb.doneAt);
  const todo = open
    .filter(cb => { const t = taskTabOf(cb, now); return t === 'overdue' || t === 'today'; })
    // Quá hạn trước → việc gấp (bất thường, khiếu nại) → hạn sớm
    .sort((a, b) => Number(b.dueAt < now) - Number(a.dueAt < now) || TYPE_RANK[taskTypeOf(a)] - TYPE_RANK[taskTypeOf(b)] || a.dueAt - b.dueAt);
  const overdue = todo.filter(cb => cb.dueAt < now);

  const complaints = complaintsOf(s, { status: 'active' }, now).filter(cb => !who || assigneeOf(s, cb) === who);
  const surveysDue = pendingSurveys(s, now).filter(p => p.dueAt <= now && ownsCustomer(p.appointment.customerId));
  const appointments = s.appointments
    .filter(a => (a.status === 'new' || a.status === 'confirmed') && a.at >= now && a.at <= now + DAY && ownsCustomer(a.customerId))
    .sort((a, b) => a.at - b.at);

  const surveysIn = (from: number, to: number) => s.surveys.filter(sv => sv.at >= from && sv.at <= to && ownsCustomer(sv.customerId));
  const csat = surveyStats(surveysIn(now - 30 * DAY, now));
  const csatPrev = surveyStats(surveysIn(now - 60 * DAY, now - 30 * DAY));

  // Khẩn: bất thường sau dịch vụ sắp / đã tới hạn, khiếu nại nghiêm trọng hoặc quá hạn SLA
  const urgent = open.filter(cb => {
    const m = complaintMetaOf(cb);
    return (isMedicalTask(cb) && cb.dueAt <= now + HOUR) || (!!m && (m.severity === 'high' || cb.dueAt < now));
  }).sort((a, b) => a.dueAt - b.dueAt);

  return {
    calls: { today: callsToday.length, yesterday: callsYesterday.length, missedRate: callsToday.length ? Math.round((missed / callsToday.length) * 100) : 0 },
    todo, overdue, open,
    doneToday: tasks.filter(cb => (cb.doneAt || 0) >= today).length,
    complaints, complaintsOverdue: complaints.filter(cb => cb.dueAt < now).length,
    surveysDue, appointments, csat, csatPrev, urgent,
  };
}
